import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Plus, X, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Users } from 'lucide-react';
import { PersonMail } from '../../types';
import { stalkerAPI, getStaticFileUrl } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { STATUS_MAP } from '../../utils/enums';
import loggingService from '../../services/loggingService';

interface EmailWithPerson extends PersonMail {
	person_name?: string;
	person_surname?: string;
	person_hideHome?: string;
	person_avatar?: string;
}

const Emails: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const navigate = useNavigate();
	const [emails, setEmails] = useState<EmailWithPerson[]>([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [showHidden, setShowHidden] = useState(false);
	const [editingEmail, setEditingEmail] = useState<EmailWithPerson | null>(null);
	const [formData, setFormData] = useState({
		pm_personId: '',
		pm_mail: '',
		pm_status: 'active'
	});
	const [persons, setPersons] = useState<any[]>([]);
	const [sortField, setSortField] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const hasLoggedPageView = useRef(false);

	useEffect(() => {
		loadData();
		loadPersons();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/stalker/emails', 'Stalker', 'Emails');
			hasLoggedPageView.current = true;
		}
	}, []);

	const loadData = async () => {
		try {
			const response = await stalkerAPI.emails.getAll();
			setEmails(response.data.data);
		} catch (error) {
			console.error('Error loading emails:', error);
		} finally {
			setLoading(false);
		}
	};

	const loadPersons = async () => {
		try {
			const response = await stalkerAPI.persons.getAll();
			setPersons(response.data.data);
		} catch (error) {
			console.error('Error loading persons:', error);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			if (editingEmail) {
				await stalkerAPI.emails.update(editingEmail.pm_id, formData);
				loggingService.logEdit('email', editingEmail.pm_id, formData.pm_mail, 'Stalker');
			} else {
				const response = await stalkerAPI.emails.create({
					...formData,
					pm_personId: Number(formData.pm_personId)
				});
				const emailId = response.data.data?.pm_id;
				if (emailId) {
					loggingService.logCreate('email', emailId, formData.pm_mail, 'Stalker');
				}
			}
			setShowModal(false);
			setEditingEmail(null);
			setFormData({ pm_personId: '', pm_mail: '', pm_status: 'active' });
			loadData();
		} catch (error) {
			console.error('Error saving email:', error);
		}
	};

	const handleDelete = async (id: number) => {
		if (!window.confirm('Are you sure you want to delete this email?')) return;
		try {
			const email = emails.find(e => e.pm_id === id);
			await stalkerAPI.emails.delete(id);
			if (email) {
				loggingService.logDelete('email', id, email.pm_mail || 'Unknown', 'Stalker');
			}
			loadData();
		} catch (error) {
			console.error('Error deleting email:', error);
		}
	};

	const filteredEmails = emails.filter(email => {
		const matchesSearch = globalSearchTerm === '' ||
			email.pm_mail?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			email.person_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			email.person_surname?.toLowerCase().includes(globalSearchTerm.toLowerCase());
		const matchesHideFilter = showHidden || email.person_hideHome !== '1';
		return matchesSearch && matchesHideFilter;
	});

	const sortedEmails = [...filteredEmails].sort((a, b) => {
		if (!sortField) return 0;
		
		let aValue: any = a[sortField as keyof EmailWithPerson];
		let bValue: any = b[sortField as keyof EmailWithPerson];
		
		// Handle nested person fields
		if (sortField === 'person_name') {
			aValue = a.person_name || '';
			bValue = b.person_name || '';
		} else if (sortField === 'person_surname') {
			aValue = a.person_surname || '';
			bValue = b.person_surname || '';
		}
		
		if (aValue === undefined || aValue === null) aValue = '';
		if (bValue === undefined || bValue === null) bValue = '';
		
		if (typeof aValue === 'string') {
			aValue = aValue.toLowerCase();
			bValue = bValue.toLowerCase();
		}
		
		if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
		if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
		return 0;
	});

	const handleSort = (field: string) => {
		if (sortField === field) {
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else if (sortDirection === 'desc') {
				// 3rd click: reset to default
				setSortField(null);
				setSortDirection('asc');
			}
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	const SortIcon = ({ field }: { field: string }) => {
		if (sortField !== field) {
			return <ArrowUpDown className="w-4 h-4 ml-1" />;
		}
		return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4 ml-1" /> : <ArrowDown className="w-4 h-4 ml-1" />;
	};

	if (loading) {
		return (
			<div className={`flex flex-col items-center justify-center h-64 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
				<div className="flex items-center space-x-2">
					<div className="w-1 h-3 bg-current animate-pulse" />
					<span>[ FETCHING_EMAIL_RECORDS... ]</span>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4 px-2 md:px-0 pb-20">
			{/* Header */}
			<div className={`flex flex-col md:flex-row items-start justify-between md:items-center p-4 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} gap-4`}>
				<div>
					<h1 className={`text-sm font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
						// SYS: CONTACT_EMAILS
					</h1>
					<p className={`text-[10px] font-mono mt-0.5 uppercase tracking-wider ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
						{globalSearchTerm ? `MATCHING_RECORDS: ${sortedEmails.length} / ${filteredEmails.length}` : `TOTAL_RECORDS: ${filteredEmails.length}`}
					</p>
				</div>
				<div className="flex items-center space-x-2 self-end md:self-auto flex-wrap">
					<button
						onClick={() => setShowHidden(!showHidden)}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border transition-colors ${
							isDark 
								? showHidden ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-[#0a0a0a] border-[#333] text-neutral-500 hover:text-white' 
								: showHidden ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-white border-neutral-300 text-neutral-600 hover:text-black'
						}`}
						title={showHidden ? 'HIDE_SECURE_RECORDS' : 'SHOW_SECURE_RECORDS'}
					>
						{showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
						<span>{showHidden ? 'SECURE_VISIBLE' : 'SECURE_HIDDEN'}</span>
					</button>
					<button
						onClick={() => {
							setEditingEmail(null);
							setFormData({ pm_personId: '', pm_mail: '', pm_status: 'active' });
							setShowModal(true);
						}}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border text-emerald-500 hover:bg-emerald-500/10 transition-colors ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}
					>
						<Plus className="w-3 h-3" />
						<span>ADD_RECORD</span>
					</button>
				</div>
			</div>

			<div className={`border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<h2 className={`text-[10px] font-mono tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#222]' : 'text-emerald-700 border-neutral-200'}`}>
					// DATA_TABLE: EMAILS
				</h2>
				<div className={`border overflow-hidden ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<div className="overflow-x-auto">
						<table className="w-full text-[10px] font-mono whitespace-nowrap">
							<thead className={isDark ? 'bg-[#0a0a0a] border-b border-[#222]' : 'bg-neutral-50 border-b border-neutral-200'}>
								<tr>
									<th 
										className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
										onClick={() => handleSort('pm_mail')}
									>
										<div className="flex items-center">
											EMAIL_ADDRESS
											<SortIcon field="pm_mail" />
										</div>
									</th>
									<th 
										className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
										onClick={() => handleSort('person_name')}
									>
										<div className="flex items-center">
											LINKED_ENTITY
											<SortIcon field="person_name" />
										</div>
									</th>
									<th 
										className={`px-4 py-2 text-left font-normal uppercase cursor-pointer select-none transition-colors ${isDark ? 'text-neutral-500 hover:bg-[#1a1a1a]' : 'text-neutral-400 hover:bg-neutral-100'}`}
										onClick={() => handleSort('pm_status')}
									>
										<div className="flex items-center">
											STATUS_CODE
											<SortIcon field="pm_status" />
										</div>
									</th>
									<th className={`px-4 py-2 text-right font-normal uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										ACTIONS
									</th>
								</tr>
							</thead>
							<tbody className={`divide-y ${isDark ? 'divide-[#222]' : 'divide-neutral-200'}`}>
								{sortedEmails.length > 0 ? (
									sortedEmails.map((email) => (
										<tr key={email.pm_id} className={`hover:bg-opacity-50 transition-colors ${showHidden && email.person_hideHome === '1' ? (isDark ? 'hover:bg-amber-900/10 bg-amber-900/5 border-l-2 border-amber-500/30' : 'hover:bg-amber-50/50 bg-amber-50/30 border-l-2 border-amber-400/40') : (isDark ? 'hover:bg-[#1a1a1a] bg-[#111]' : 'hover:bg-neutral-50 bg-white')}`}>
											<td className={`px-4 py-2 uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
												<div className="flex items-center gap-2">
													{email.pm_mail}
													{showHidden && email.person_hideHome === '1' && (
														<span className={`text-[8px] font-mono px-1 py-0.5 border ${isDark ? 'border-amber-500/40 text-amber-400 bg-amber-500/10' : 'border-amber-400 text-amber-600 bg-amber-50'}`}>
															SECURE
														</span>
													)}
												</div>
											</td>
											<td className={`px-4 py-2`}>
												{email.person_name && email.person_surname ? (
													<div className="flex items-center space-x-2">
														{email.person_avatar ? (
															<img
																src={getStaticFileUrl(email.person_avatar) || ''}
																alt="AVT"
																className={`w-6 h-6 object-cover border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}
															/>
														) : (
															<div className={`w-6 h-6 flex items-center justify-center font-mono text-[8px] border ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
																{(email.person_name || '').charAt(0)}{(email.person_surname || '').charAt(0)}
															</div>
														)}
														<button
															onClick={() => navigate(`/stalker/persons/${email.pm_personId}`)}
															className={`uppercase font-bold tracking-wider hover:underline ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}
														>
															{email.person_name} {email.person_surname}
														</button>
													</div>
												) : (
													<span className={`uppercase tracking-wider ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ UNKNOWN_ENTITY ]</span>
												)}
											</td>
											<td className="px-4 py-2">
												<span className={`font-bold uppercase tracking-wider text-[10px] font-mono ${
													(email.pm_status || '').toLowerCase() === 'active'
														? (isDark ? 'text-emerald-400' : 'text-emerald-600')
														: (isDark ? 'text-red-400' : 'text-red-600')
												}`}>
													{STATUS_MAP[email.pm_status as keyof typeof STATUS_MAP] || email.pm_status}
												</span>
											</td>
											<td className="px-4 py-2 text-right">
												<div className="flex items-center justify-end space-x-1">
													<button
														onClick={() => {
															setEditingEmail(email);
															setFormData({
																pm_personId: String(email.pm_personId),
																pm_mail: email.pm_mail,
																pm_status: email.pm_status
															});
															setShowModal(true);
														}}
														className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white/90 border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
														title="MODIFY"
													>
														<Edit className="w-3.5 h-3.5" />
													</button>
													<button
														onClick={() => handleDelete(email.pm_id)}
														className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-red-900/50 text-red-500 hover:text-red-400' : 'bg-white/90 border-neutral-300 hover:bg-red-50 text-red-500 hover:text-red-600'}`}
														title="PURGE"
													>
														<Trash2 className="w-3.5 h-3.5" />
													</button>
												</div>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td colSpan={4} className={`px-4 py-8 text-center uppercase tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
											[ NO_RECORDS_FOUND ]
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Modal */}
			{showModal && createPortal(
				<div
					className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowModal(false)}
				>
					<div
						className={`border p-6 w-full max-w-md ${isDark ? 'bg-[#111] border-[#333]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className={`flex justify-between items-center mb-6 pb-2 border-b ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}>
							<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
								{editingEmail ? '// EDIT_RECORD' : '// NEW_RECORD'}
							</h2>
							<button
								onClick={() => {
									setShowModal(false);
									setEditingEmail(null);
									setFormData({ pm_personId: '', pm_mail: '', pm_status: 'active' });
								}}
								className={`p-1 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'border-neutral-200 hover:bg-neutral-100 text-neutral-500 hover:text-black'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handleSubmit} className="space-y-4 font-mono text-[10px] uppercase tracking-wider">
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									LINKED_ENTITY <span className="text-emerald-500">*</span>
								</label>
								<select
									value={formData.pm_personId}
									onChange={(e) => setFormData({ ...formData, pm_personId: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
									required
									disabled={!!editingEmail}
								>
									<option value="">[ SELECT_ENTITY ]</option>
									{persons.map((person) => (
										<option key={person.person_id} value={person.person_id}>
											{person.person_name} {person.person_surname}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
									EMAIL_ADDRESS <span className="text-emerald-500">*</span>
								</label>
								<input
									type="email"
									value={formData.pm_mail}
									onChange={(e) => setFormData({ ...formData, pm_mail: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors placeholder:text-neutral-600 ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
									placeholder="e.g. unknown@domain.com"
									required
								/>
							</div>
							<div>
								<label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>STATUS_CODE</label>
								<select
									value={formData.pm_status}
									onChange={(e) => setFormData({ ...formData, pm_status: e.target.value })}
									className={`w-full px-3 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] focus:border-emerald-500/50 text-neutral-200' : 'bg-neutral-50 border-neutral-300 focus:border-emerald-500/50 text-neutral-800'}`}
								>
									<option value="active">ACTIVE</option>
									<option value="notactive">NOT_ACTIVE</option>
								</select>
							</div>
							<div className="flex justify-end space-x-2 pt-4">
								<button
									type="button"
									onClick={() => {
										setShowModal(false);
										setEditingEmail(null);
										setFormData({ pm_personId: '', pm_mail: '', pm_status: 'active' });
									}}
									className={`px-4 py-2 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
								>
									CANCEL
								</button>
								<button
									type="submit"
									className={`px-4 py-2 border transition-colors ${isDark ? 'bg-[#0a0a0a] border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10' : 'bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100'}`}
								>
									{editingEmail ? 'EXECUTE_UPDATE' : 'EXECUTE_CREATE'}
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default Emails;

